/**
 * @fileoverview Input/output schemas for the prose-comparer-committee-v2 agent.
 *
 * This composite agent runs prose-comparer-v2 with 5 different models in parallel
 * and aggregates results via majority voting.
 */

import { z } from 'zod';
import { CompositeBaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { OptionEvaluationSchema } from '@/ai/agents/primitives/prose/prose-comparer-v2/schema';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 * Same as prose-comparer-v2 - we pass this to each parallel execution.
 */
export const InputSchema = CompositeBaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.originalPrompt.schema)
  .merge(AGENT_SCHEMA_OBJECTS.evalOptions.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .merge(AGENT_SCHEMA_OBJECTS.genre.schema)
  .merge(AGENT_SCHEMA_OBJECTS.genreGuidance.schema);

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * A single vote from one model in the committee.
 */
export const CommitteeVoteSchema = z.object({
  /** The model that cast this vote */
  model: z.string().describe('The AI model identifier that cast this vote'),
  /** Display label for the model */
  modelLabel: z.string().describe('Human-readable label for the model'),
  /** The option this model voted for */
  votedFor: z.string().describe('The name of the option this model voted for'),
  /** The justification for this vote */
  justification: z.string().describe('The reasoning behind this vote'),
  /** Per-option evaluations from this voter (rubric-based commentary) */
  evaluations: z.array(OptionEvaluationSchema).default([]).describe(
    'Per-option rubric evaluations produced by this voter, one entry per option.'
  ),
});

export type CommitteeVote = z.infer<typeof CommitteeVoteSchema>;

/**
 * Output includes the winning option plus vote breakdown.
 */
export const OutputSchema = z.object({
  /** The name of the winning option (plurality winner) */
  winningModelName: z.string().describe(
    'The name of the option that received the most votes.'
  ),
  /** Combined justifications from models that voted for the winner */
  justification: z.string().describe(
    'Combined reasoning from the models that voted for the winning option.'
  ),
  /** Vote count for the winning option */
  winningVoteCount: z.number().describe('Number of votes for the winning option'),
  /** Total votes cast (successful model runs) */
  totalVotes: z.number().describe('Total number of votes cast'),
  /** Breakdown of all votes by model */
  votes: z.array(CommitteeVoteSchema).describe('Individual votes from each committee member'),
  /** Models that failed to produce results */
  failed: z.array(z.string()).describe('Model identifiers that failed to vote'),
  /** Total execution time */
  durationMs: z.number().describe('Total parallel execution time in milliseconds'),
});

export type Output = z.infer<typeof OutputSchema>;
